import { GLOBAL_AGENTS_SID } from "@app/types/assistant/assistant";

export const peopleSidebarGroupLimit = 12;

// The group of threads you hold with no other human. Its canonical key is the
// empty set, which no URL segment or query value can carry, so the wire uses
// this token instead. A generated sId never collides with it.
export const peopleSoloGroupToken = "me";

export const peopleSoloGroupLabel = "Friday (You)";

export function isAgentParticipantId(id: string): boolean {
  return id.startsWith("agent_");
}

export function parseAgentConfigurationId(id: string): string | null {
  return id.startsWith("agent_") ? id.slice("agent_".length) : null;
}

export function formatAgentParticipantId(agentConfigurationId: string): string {
  return `agent_${agentConfigurationId}`;
}

export const guideParticipantId = formatAgentParticipantId(
  GLOBAL_AGENTS_SID.HELPER
);

const groupKeySeparator = ",";

export function canonicalizePeopleGroupIds(userIds: string[]): string[] {
  return Array.from(new Set(userIds.filter(Boolean))).sort();
}

export function peopleGroupKey(userIds: string[]): string {
  return canonicalizePeopleGroupIds(userIds).join(groupKeySeparator);
}

export function parsePeopleGroupKey(key: string): string[] {
  return key.length === 0 ? [] : key.split(groupKeySeparator);
}

export function encodePeopleGroupIds(userIds: string[]): string {
  const canonical = canonicalizePeopleGroupIds(userIds);
  return canonical.length === 0
    ? peopleSoloGroupToken
    : canonical.join(groupKeySeparator);
}

/**
 * The inverse of `encodePeopleGroupIds`, for a URL segment or query value.
 * Returns `[]` for the solo group and `null` when nothing usable was supplied,
 * so a caller can tell "just me" from "no group named".
 */
export function decodePeopleGroupIds(
  value: string | null | undefined
): string[] | null {
  if (!value) {
    return null;
  }
  if (value === peopleSoloGroupToken) {
    return [];
  }

  const ids = canonicalizePeopleGroupIds(
    value.split(groupKeySeparator).map((userId) => userId.trim())
  );

  return ids.length === 0 ? null : ids;
}

export function formatPeopleGroupLabel(
  humans: Array<{ sId: string; fullName: string }>
): string {
  // Sort here (rather than trusting caller order) so the label is stable no
  // matter what order humans are fetched or supplied in; matches the plain
  // lexicographic order canonicalizePeopleGroupIds uses for the key.
  const ordered = [...humans].sort((a, b) =>
    a.sId < b.sId ? -1 : a.sId > b.sId ? 1 : 0
  );

  if (ordered.length <= 2) {
    return ordered.map((human) => human.fullName).join(", ");
  }

  const [first, second] = ordered;
  return `${first.fullName}, ${second.fullName} +${ordered.length - 2}`;
}
